import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";

const LOCAL_DEMO = import.meta.env.VITE_LOCAL_DEMO === "true";
const DEV_LOGIN_EMAIL = import.meta.env.DEV ? import.meta.env.VITE_DEV_LOGIN_EMAIL || "" : "";
const DEV_LOGIN_PASSWORD = import.meta.env.DEV ? import.meta.env.VITE_DEV_LOGIN_PASSWORD || "" : "";

export default function LoginPage() {
  const [email, setEmail] = useState(DEV_LOGIN_EMAIL);
  const [password, setPassword] = useState(DEV_LOGIN_PASSWORD);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registered = searchParams.get("registered") === "true";
  const expired = searchParams.get("expired") === "true";

  useEffect(() => {
    if (!loading && isAuthenticated) navigate("/app", { replace: true });
  }, [loading, isAuthenticated, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (result.success) {
      navigate("/app", { replace: true });
    } else {
      setError(result.error || "Login failed");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 pt-16">
      <div className="w-full max-w-md">
        <div className="rounded-xl border border-border bg-card p-8 shadow-lg">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-foreground hover:opacity-90">
              <img src="/logo.png" alt="CompostDeveloper" className="h-10 w-10 rounded" />
              <span className="text-xl font-bold">
                Compost<span className="text-forest-500">Developer</span>
              </span>
            </Link>
            <h1 className="mt-6 text-2xl font-semibold text-foreground">Sign in</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {LOCAL_DEMO
                ? "Isolated local demo. This password is not the iPhone or staging password."
                : "Enter your credentials to access your account."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {expired && (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-sm text-amber-400">
                Your session has expired. Please sign in again.
              </div>
            )}
            {registered && (
              <div className="rounded-lg bg-forest-500/10 border border-forest-500/20 px-4 py-3 text-sm text-forest-400">
                Account created successfully! Sign in to get started.
              </div>
            )}
            {error && (
              <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-sm"
                placeholder="you@facility.com"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-11 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-sm"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="mt-1.5 text-right">
                <Link to="/forgot-password" className="text-xs text-forest-500 hover:text-forest-400">
                  Forgot password?
                </Link>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 px-4 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/register" className="text-forest-500 hover:text-forest-400 font-medium">
              Create one
            </Link>
          </p>
        </div>
        <p className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
